import {constructTableRequestParams} from "../../utils/TableUtils";
import request from 'utils/RequestManager';
import config from '../../config';
import {getUserTimezone, getUserTodayRange, toBackendDateTime, toBackendHourRange} from "../../utils/DateUtils";

const backendURL = config.BACKEND_HOST;

function appendTodayDates(params) {
    const today = getUserTodayRange();
    params.set('todayStartDate', toBackendDateTime(today.start));
    params.set('todayEndDate', toBackendDateTime(today.end));
    return params;
}

function appendExcelTimezone(params) {
    const timezone = getUserTimezone();
    if (timezone) params.set('timezone', timezone);
    else params.delete('timezone');
    return params;
}

function modalRequestParams(filters = {}, pageable) {
    const {requestToOperatorStartHour, requestToOperatorEndHour} = filters;
    const referenceDate = filters.start instanceof Date ? filters.start
        : filters.end instanceof Date ? filters.end : new Date();
    const hours = toBackendHourRange(requestToOperatorStartHour, requestToOperatorEndHour, referenceDate);
    return constructTableRequestParams({
        ...filters,
        requestToOperatorStartHour: hours.start,
        requestToOperatorEndHour: hours.end,
    }, pageable);
}

export function getStatisticNumbers(companyIds, operatorId = null) {

    const params = appendTodayDates(new URLSearchParams());

    companyIds.forEach((id) => {
        params.append('companyIds', id);
    });

    if (operatorId !== null) {
        params.append('operatorId', operatorId);
    }

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/numbers',
        params,
    })
        .then(response => response.data);
}

export function getStatisticBotNumbers(companyIds) {

    const params = new URLSearchParams();

    companyIds.forEach((id) => {
        params.append('companyIds', id);
    });

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/bots/numbers',
        params,
    })
        .then(response => response.data);
}

export function getModalData(filters = {}, pageable = {}) {

    const params = modalRequestParams(filters, pageable);

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/conversation-data',
        params
    })
        .then(response => response.data);
}

export function generateModalDataExcel(filename, filters) {

    const params = appendExcelTimezone(modalRequestParams(filters));

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/excel/modal-data',
        responseType: 'blob',
        params
    }).then(response => {
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename + '.xlsx';
        link.click();

        window.URL.revokeObjectURL(url);
    });
}

export function getBotStatisticNumbers(filters = {}, pageable = {}) {

    const params = constructTableRequestParams(filters, pageable);

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/bot-numbers',
        params
    })
        .then(response => response.data);
}

export function getOperatorsTableData(parentCompanyId, filters = {}, pageable = {}) {

    const params = appendTodayDates(constructTableRequestParams(filters, pageable));

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: `/statistic/operators/${parentCompanyId}`,
        params
    })
        .then(response => response.data);
}

/*
 * Operator of the month, for the month that has just ended.
 *
 * 204 when nobody qualified - the backend says so rather than sending a winner
 * with the name left out - so an empty body is an answer and not a failure.
 */
/*
 * The last twelve months of the award.
 *
 * No companyIds: the award belongs to the company as a whole. "Operator of the
 * month for bot number three" is a title nobody asked for, and the server keeps
 * one row per month per company accordingly.
 */
export function getOperatorOfTheMonthHistory(parentCompanyId, limit = 12) {
    return request({
        method: 'GET',
        baseURL: backendURL,
        url: `/statistic/operator-of-the-month/${parentCompanyId}/history`,
        params: {limit},
    }).then(response => response.data || []);
}

export function getOperatorOfTheMonth(parentCompanyId, companyIds) {
    const params = new URLSearchParams();
    companyIds.forEach((id) => params.append('companyIds', id));

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: `/statistic/operator-of-the-month/${parentCompanyId}`,
        params,
    }).then(response => response.data || null);
}

/*
 * What the signed-in operator's own badge is built from.
 *
 * No id in the call: the server answers for whoever the token says is asking.
 * The two screens an operator has were each assembling this themselves out of
 * three other requests, with a copy of the formula in the panel - this is that
 * copy removed.
 */
export function getMyScore() {
    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/my-score',
    })
        .then(response => response.data);
}

/*
 * The months the signed-in operator has won.
 *
 * No id: the server answers about whoever is asking, the same way getMyScore
 * does.
 */
export function getMyAwards() {
    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/my-awards',
    })
        .then(response => response.data || []);
}

export function getOperatorTableData(parentCompanyId, operatorId, filters = {}) {

    const params = appendTodayDates(constructTableRequestParams(filters));

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: `/statistic/operators/${parentCompanyId}/operator/${operatorId}`,
        params
    })
        .then(response => response.data);
}

export function getOperatorsLoginLogoutData(filters = {}, pageable = {}) {

    const params = constructTableRequestParams(filters, pageable);

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: `/statistic/operators-login-logout-data`,
        params
    })
        .then(response => response.data);
}

export function generateOperatorsLoginLogoutExcel(filename, filters) {

    const params = appendExcelTimezone(constructTableRequestParams(filters));

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/excel/operators-login-logout',
        responseType: 'blob',
        params
    }).then(response => {
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename + '.xlsx';
        link.click();

        window.URL.revokeObjectURL(url);
    });
}

export function getOperatorsTimes(companyIds, operatorIds = [],start = null,end = null) {

    const params = new URLSearchParams();

    companyIds.forEach((id) => {
        params.append('companyIds', id);
    });

    operatorIds.forEach((id) => {
        params.append('operatorIds', id);
    })

    if(start)params.append('start', toBackendDateTime(start));
    if(end)params.append('end', toBackendDateTime(end));

    return request({
        method: 'GET',
        baseURL: backendURL,
        url: '/statistic/operators/time-statistics',
        params,
    })
        .then(response => response.data);
}



// WEBPACK FOOTER //
// ./src/services/statistics/api.js